import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/**
 * Constellation map — spectrum peaks as stars, waveform as connecting dust.
 */
type Star = { x: number; y: number; bin: number; phase: number };
const stars: Star[] = [];

function ensureStars(w: number, h: number, n: number): void {
  if (stars.length === n) return;
  stars.length = 0;
  for (let i = 0; i < n; i++) {
    stars.push({
      x: (i / n) * w * 0.9 + w * 0.05,
      y: h * (0.25 + Math.random() * 0.5),
      bin: Math.floor(Math.pow(i / n, 1.4) * 200) % 256,
      phase: Math.random() * Math.PI * 2,
    });
  }
}

export const constellationViz: Visualizer = {
  id: 'constellation',
  label: 'Constellation',
  draw(frame) {
    const { ctx, w, h, freq, wave, bands, t } = frame;
    clearBg(ctx, w, h, 0.55);
    ensureStars(w, h, 72);

    // deep space
    const g = ctx.createRadialGradient(w / 2, h / 2, 10, w / 2, h / 2, Math.min(w, h) * 0.7);
    g.addColorStop(0, 'rgba(12, 24, 40, 0.5)');
    g.addColorStop(1, 'rgba(2, 4, 10, 0.98)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // waveform dust trail
    ctx.beginPath();
    const step = Math.max(2, Math.floor(wave.length / 180));
    for (let i = 0; i < wave.length; i += step) {
      const x = (i / wave.length) * w;
      const y = h * 0.55 + (wave[i] || 0) * h * 0.2 * (1 + bands.energy);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = `rgba(120,180,220,${0.12 + bands.mids * 0.2})`;
    ctx.lineWidth = 1;
    ctx.stroke();

    // connections between hot stars
    const levels = stars.map((s) => {
      const bin = Math.min(freq.length - 1, s.bin % freq.length);
      return freq[bin] / 255;
    });

    ctx.lineWidth = 1;
    for (let i = 0; i < stars.length; i++) {
      if (levels[i] < 0.35) continue;
      for (let j = i + 1; j < Math.min(i + 6, stars.length); j++) {
        if (levels[j] < 0.3) continue;
        const a = (levels[i] + levels[j]) * 0.5;
        ctx.strokeStyle = `rgba(86,240,200,${0.05 + a * 0.2})`;
        ctx.beginPath();
        ctx.moveTo(stars[i].x, stars[i].y);
        ctx.lineTo(stars[j].x, stars[j].y);
        ctx.stroke();
      }
    }

    for (let i = 0; i < stars.length; i++) {
      const s = stars[i];
      const v = levels[i];
      const pulse = 0.6 + 0.4 * Math.sin(t * 3 + s.phase + bands.beat * 4);
      const r = (1.2 + v * 7 * pulse) * (1 + bands.kick * 0.25);
      // drift lightly with bass
      const dx = Math.sin(t * 0.4 + s.phase) * bands.bass * 8;
      const dy = Math.cos(t * 0.35 + s.phase * 1.3) * bands.mids * 6;
      const x = s.x + dx;
      const y = s.y + dy + (0.5 - v) * 20;

      const glow = ctx.createRadialGradient(x, y, 0, x, y, r * 4);
      glow.addColorStop(0, `hsla(${170 + v * 40}, 95%, 70%, ${0.4 + v * 0.5})`);
      glow.addColorStop(0.4, `hsla(${190 + i}, 90%, 55%, ${0.15 + v * 0.25})`);
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(x, y, r * 4, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = `rgba(255,255,255,${0.5 + v * 0.5})`;
      ctx.beginPath();
      ctx.arc(x, y, Math.max(0.8, r * 0.35), 0, Math.PI * 2);
      ctx.fill();
    }

    if (bands.beat > 0.4) {
      ctx.fillStyle = `rgba(180,220,255,${0.03 * bands.beat})`;
      ctx.fillRect(0, 0, w, h);
    }

    drawHud(frame);
  },
};
